import { toast } from "sonner";

const WIDGET_CONTAINER_ID = "ph-conversations-widget-container";
const OPEN_CHAT_BUTTON_SELECTOR = 'button[aria-label^="Open chat"]';
const AVAILABILITY_TIMEOUT_MS = 6_000;
const OPEN_TIMEOUT_MS = 2_000;
const POLL_INTERVAL_MS = 100;

async function loadAnalytics() {
  const { analytics } = await import("@repo/analytics/client");
  return analytics;
}

function waitUntil(
  predicate: () => boolean,
  timeoutMs: number,
): Promise<boolean> {
  if (predicate()) return Promise.resolve(true);

  const startedAt = Date.now();
  return new Promise((resolve) => {
    const timer = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, POLL_INTERVAL_MS);
  });
}

function isSupportChatOpen(): boolean {
  const container = document.getElementById(WIDGET_CONTAINER_ID);
  if (!container) return false;
  return !container.querySelector(OPEN_CHAT_BUTTON_SELECTOR);
}

function clickOpenChatButton(): boolean {
  const container = document.getElementById(WIDGET_CONTAINER_ID);
  const button = container?.querySelector<HTMLButtonElement>(
    OPEN_CHAT_BUTTON_SELECTOR,
  );
  if (!button) return false;
  button.click();
  return true;
}

export async function openPosthogSupport(): Promise<boolean> {
  try {
    const analytics = await loadAnalytics();
    const { conversations } = analytics.rawClient;
    const ready = await waitUntil(() => {
      try {
        return conversations.isAvailable();
      } catch {
        return false;
      }
    }, AVAILABILITY_TIMEOUT_MS);
    if (!ready) {
      toast.error("Couldn’t open Report issue. Try again in a moment.");
      return false;
    }

    if (isSupportChatOpen()) return true;

    conversations.show();
    const opened = await waitUntil(() => {
      if (isSupportChatOpen()) return true;
      clickOpenChatButton();
      return isSupportChatOpen();
    }, OPEN_TIMEOUT_MS);

    if (!opened) {
      toast.error("Couldn’t open Report issue. Try again in a moment.");
      return false;
    }
    return true;
  } catch {
    toast.error("Couldn’t open Report issue. Try again in a moment.");
    return false;
  }
}

export function buildPosthogSupportInboxUrl(): string | null {
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  if (!host) return null;

  try {
    const url = new URL(host);
    const origin = `${url.protocol}//${url.host
      .replace(".i.posthog.com", ".posthog.com")
      .replace(/^app\./, "")}`;
    const projectId = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_ID;
    if (projectId) {
      return `${origin}/project/${projectId}/support`;
    }
    return `${origin}/support`;
  } catch {
    return null;
  }
}
